/**
 * MODULE_CONFIG: per-module content for the shared ModuleShowcasePage.
 * Hero copy and features are derived from the previous standalone module pages
 * under src/pages/modules/Asap*.jsx. Screenshots reuse existing Frame{5..8}.png
 * assets so no new image generation is needed for Phase 2.
 *
 * Image imports happen in the page module that consumes this data so this file
 * stays purely declarative.
 */

import Frame5 from "../../assets/images/Frame5.png";
import Frame6 from "../../assets/images/Frame6.png";
import Frame7 from "../../assets/images/Frame7.png";
import Frame8 from "../../assets/images/Frame8.png";
import Frame9 from "../../assets/images/Frame9.png";

export const MODULE_KEYS = ["profile", "partners", "skills", "pilot", "cv"];

// URL slug used in the canonical /modules/asap-<slug> path. "profile" is
// rendered as "profil" to match the historical FR-language URL.
export const MODULE_SLUG = {
  cv: "cv",
  skills: "skills",
  pilot: "pilot",
  partners: "partners",
  profile: "profil",
};

export const modulePath = (moduleKey) => `/modules/asap-${MODULE_SLUG[moduleKey]}`;

export const MODULE_CONFIG = {
  cv: {
    label: "ASAP CV",
    hero: {
      eyebrow: "Module disponible",
      title: "Transformez les CV en dossiers de compétences en moins de 2 minutes",
      lead: "Importez un CV brut, ASAP CV produit un dossier de compétences enrichi à votre charte, prêt à être envoyé au client.",
    },
    features: [
      { icon: "CV", title: "Import multi-format", desc: "Glissez vos CV en DOCX ou PDF. ASAP CV reconnaît automatiquement la structure et extrait les informations clés." },
      { icon: "AI", title: "Enrichissement IA", desc: "Reformulation, harmonisation du ton et complétion des compétences manquantes selon votre style maison." },
      { icon: "TPL", title: "Votre template, à la lettre", desc: "Le dossier final respecte votre charte graphique : police, couleurs, sections et ordre des rubriques." },
    ],
    showcase: { src: Frame6, alt: "Dossier de compétences ASAP CV au format ESN" },
    useCases: [
      { kicker: "Avant-vente", title: "Répondre vite à un appel d'offres", desc: "Générer en quelques minutes une shortlist de dossiers prêts à présenter au client." },
      { kicker: "Recrutement", title: "Standardiser le rendu", desc: "Tous les CV reçus passent au même format, plus simple à comparer." },
      { kicker: "Multilingue", title: "Traduire FR ↔ EN", desc: "Bascule rapide entre le français et l'anglais sans repasser par un prestataire externe." },
      { kicker: "Préparation", title: "Brief candidat", desc: "Génération automatique d'un guide oral et de questions-clés pour préparer l'entretien client." },
    ],
    faq: [
      { q: "Quels formats de fichiers puis-je importer ?", a: "DOCX et PDF, les deux sont nativement supportés." },
      { q: "Combien de temps pour générer un dossier ?", a: "Moins de 2 minutes en général, le temps que l'IA reformate et reformule selon votre template." },
      { q: "Puis-je changer de template en cours d'année ?", a: "Oui, sans frais supplémentaires : votre template peut évoluer à tout moment." },
      { q: "Peut-on traduire un CV du français vers l'anglais ?", a: "Oui, et inversement, depuis la même interface d'intégration." },
    ],
  },

  skills: {
    label: "ASAP Skills",
    hero: {
      eyebrow: "Module disponible",
      title: "Cartographiez les compétences de vos consultants en quelques minutes",
      lead: "ASAP Skills évalue le niveau réel de vos candidats grâce à des tests techniques personnalisés, anti-triche, et des rapports détaillés.",
    },
    features: [
      { icon: "EX", title: "Tests sur-mesure", desc: "Bibliothèque de questions, IDE intégré et création de tests adaptés à chaque besoin client." },
      { icon: "AT", title: "Anti-triche embarqué", desc: "Détection automatique des comportements suspects et verrouillage de l'environnement de test." },
      { icon: "RP", title: "Rapports actionnables", desc: "Score global, axes forts et faibles : un rapport prêt à présenter au client." },
    ],
    showcase: { src: Frame7, alt: "Rapport d'évaluation ASAP Skills par candidat" },
    useCases: [
      { kicker: "Présélection", title: "Filtrer le pipeline", desc: "Évaluer 30 candidats en parallèle et ne garder que les profils qualifiés." },
      { kicker: "Avant entretien client", title: "Sécuriser le placement", desc: "Objectiver le niveau réel pour éviter une déconvenue en entretien." },
      { kicker: "Inter-contrat", title: "Réviser les compétences", desc: "Les consultants en intercontrat peuvent se ré-évaluer pour mieux se positionner." },
      { kicker: "Recrutement", title: "Calibrer un poste", desc: "Comparer les profils sur une base technique commune." },
    ],
    faq: [
      { q: "Combien de tests sont inclus ?", a: "10 tests par mois sur Starter, 30 sur Team, illimité sur Custom (annuel)." },
      { q: "Le nombre d'utilisateurs est-il limité ?", a: "Non. Le nombre d'utilisateurs internes est illimité quel que soit le plan." },
      { q: "Comment fonctionne l'IDE ?", a: "Un environnement de code intégré, identique à celui que le candidat utiliserait en mission, est embarqué dans chaque test." },
      { q: "Que contient le rapport final ?", a: "Score global par compétence, points forts et axes d'amélioration, code source produit pendant le test." },
    ],
  },

  pilot: {
    label: "ASAP Pilot",
    hero: {
      eyebrow: "Module disponible",
      title: "Pilotez votre activité depuis une vision unique : clients, besoins, consultants",
      lead: "ASAP Pilot centralise CRM, gestion des temps, suivi RH et matching profils/besoins. Une seule plateforme pour piloter votre ESN.",
    },
    features: [
      { icon: "CR", title: "CRM intégré", desc: "Clients, besoins, opportunités, suivi commercial : tout est rattaché au profil de chaque consultant." },
      { icon: "MA", title: "Matching profils/besoins", desc: "L'IA propose les bons consultants pour chaque besoin client en fonction des compétences et de la disponibilité." },
      { icon: "KP", title: "KPI & performance", desc: "Tableaux de bord temps réel : pipeline, taux d'activation, performance commerciale." },
    ],
    showcase: { src: Frame5, alt: "Tableau de bord ASAP Pilot avec KPIs et matching" },
    useCases: [
      { kicker: "Direction", title: "Lire la capacité", desc: "Voir d'un coup d'œil les consultants disponibles, les besoins ouverts et les marges projetées." },
      { kicker: "Staffing", title: "Affecter rapidement", desc: "Match automatique entre besoins et profils internes, avec scoring." },
      { kicker: "Commerce", title: "Suivre les opportunités", desc: "Pipeline visuel, relances automatiques et historique client centralisé." },
      { kicker: "RH", title: "Gestion des temps", desc: "Saisie d'activité, congés, absences : tout est rattaché à la mission." },
    ],
    faq: [
      { q: "Comment se passe la facturation ?", a: "59€ par manager et par mois, plus 4€ par consultant suivi. Le plan Custom permet une tarification volume." },
      { q: "Peut-on importer notre base existante ?", a: "Oui : import CSV des clients, consultants et besoins. L'équipe Pilot accompagne la migration." },
      { q: "Le scoring ATS est-il inclus ?", a: "Oui, le matching profils/besoins fonctionne sur les CV importés et les profils internes." },
      { q: "Y a-t-il une intégration calendrier ?", a: "Oui, synchronisation Google Calendar et Outlook pour les entretiens et missions." },
    ],
  },

  partners: {
    label: "ASAP Partners",
    hero: {
      eyebrow: "Module disponible",
      title: "Activez votre réseau de partenaires sur chaque opportunité",
      lead: "ASAP Partners centralise vos sociétés partenaires, diffuse vos appels d'offres, qualifie automatiquement les CV reçus et calcule vos marges.",
    },
    features: [
      { icon: "DI", title: "Diffusion automatisée", desc: "Envoi groupé de votre besoin à votre vivier de partenaires, suivi des réponses centralisé." },
      { icon: "QA", title: "Qualification IA des CV", desc: "Filtre automatique des CV reçus selon votre besoin, scoring et tri automatique." },
      { icon: "MA", title: "Calcul des marges", desc: "Marges TJM, marges projetées sur la mission et tableau de bord financier intégré." },
    ],
    showcase: { src: Frame8, alt: "Diffusion d'appel d'offres aux partenaires ASAP" },
    useCases: [
      { kicker: "Avant-vente", title: "Élargir le sourcing", desc: "Diffuser un besoin à 30 partenaires en un clic, recevoir 50 CV en 24h." },
      { kicker: "Inter-contrat", title: "Pousser nos profils", desc: "Diffusion automatique des CV en intercontrat à vos sociétés partenaires (sous-traitance)." },
      { kicker: "Direction", title: "Piloter les marges", desc: "Suivi en temps réel des marges sur les missions sous-traitées." },
      { kicker: "Réseau", title: "Garder un vivier vivant", desc: "Relances automatiques, classement par catégorie, historique de collaboration." },
    ],
    faq: [
      { q: "Combien de partenaires puis-je gérer ?", a: "Illimité. La tarification est par Business Unit, pas par nombre de partenaires." },
      { q: "Les CV reçus sont-ils filtrés automatiquement ?", a: "Oui, le scoring IA classe les candidats par pertinence et applique vos critères de filtrage." },
      { q: "Comment se calcule la marge ?", a: "Différence TJM facturé client - TJM acheté partenaire, automatiquement, par mission et par mois." },
      { q: "Puis-je connecter mon CRM ?", a: "Oui, intégrations CRM standard (Salesforce, HubSpot, Pipedrive) ou via API." },
    ],
  },

  profile: {
    label: "ASAP Profile",
    hero: {
      eyebrow: "Sourcing automatisé",
      title: "Trouvez le bon profil pour chaque appel d'offres en moins de 5 minutes",
      lead: "ASAP Profile transforme un cahier des charges en shortlist enrichie : sourcing multi-sources, scoring de pertinence et export prêt à présenter.",
    },
    features: [
      { icon: "RF", title: "RFP → shortlist", desc: "Importez un appel d'offres, ASAP Profile génère automatiquement les requêtes de sourcing adaptées." },
      { icon: "SO", title: "Sourcing multi-sources", desc: "Recherche cross-plateforme, signaux d'expérience et de séniorité agrégés." },
      { icon: "SC", title: "Scoring & enrichissement", desc: "Chaque profil est scoré par rapport au besoin et enrichi avec les compétences détectées." },
    ],
    showcase: { src: Frame9, alt: "Shortlist ASAP Profile à partir d'un appel d'offres" },
    useCases: [
      { kicker: "Avant-vente", title: "Répondre à une RFP urgente", desc: "Charger l'appel d'offres, recevoir 10 profils alignés en moins de 5 minutes." },
      { kicker: "Sourcing", title: "Élargir le pool", desc: "Découvrir des profils en dehors de votre vivier interne, avec contexte enrichi." },
      { kicker: "Direction", title: "Évaluer la faisabilité", desc: "Avant de répondre, vérifier que le marché contient bien des profils pour le besoin." },
      { kicker: "Recrutement", title: "Construire un pool", desc: "Constituer un vivier qualifié sur une compétence rare." },
    ],
    faq: [
      { q: "Combien de RFPs sont inclus ?", a: "10 par mois sur la formule 10 RFPs (170€), 20 par mois sur 20 RFPs (300€), volume sur mesure en Custom." },
      { q: "D'où viennent les profils sourcés ?", a: "Sourcing multi-sources via l'API People Data Labs (PDL). Les profils sont enrichis avec les compétences détectées." },
      { q: "Comment est calculé le score de pertinence ?", a: "Une fois la recherche lancée, le score est calculé côté ASAP Hub à partir des compétences détectées et de la séniorité." },
      { q: "Peut-on exporter la shortlist ?", a: "Oui, export en CSV, ou directement vers ASAP CV pour générer les dossiers de compétences." },
    ],
  },
};
